import { useEffect, useState } from "react";
import GoBack from "../components/GoBack";
import useFlow from "../hooks/useFlow";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

export default function ClassEventList({ type }) {
  const {
    selectedSiteId,
    selectedLocationId,
    loadSchedules,
    setSelectedSchedule,
    timezoneRef,
  } = useFlow();
  const navigate = useNavigate();

  const [schedules, setSchedules] = useState([]);
  const [passedSchedules, setPassedSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    moment.tz(timezoneRef.current).format("YYYY-MM-DD"),
  );
  const [dataCache, setDataCache] = useState({}); // Cache for loaded data
  const [loadingDates, setLoadingDates] = useState(new Set()); // Track which dates are loading

  const today = moment.tz(timezoneRef.current).format("YYYY-MM-DD");
  const maxDate = moment
    .tz(timezoneRef.current)
    .add(7, "days")
    .format("YYYY-MM-DD");

  const canGoBack = selectedDate > today;
  const canGoForward = selectedDate < maxDate;

  const formatDisplayDate = (dateStr) => {
    const date = moment.tz(dateStr, timezoneRef.current);
    const today = moment.tz(timezoneRef.current);

    if (date.isSame(today, "day")) {
      return "Today";
    } else if (date.isSame(today.add(1, "day"), "day")) {
      return "Tomorrow";
    } else {
      return date.format("ddd, MMM D");
    }
  };

  // "today" / "tomorrow" / "on Thu, Aug 6" — used in the empty-state message
  const formatRelativeDay = (dateStr) => {
    const date = moment.tz(dateStr, timezoneRef.current);
    const now = moment.tz(timezoneRef.current);

    if (date.isSame(now, "day")) {
      return "today";
    } else if (date.isSame(moment(now).add(1, "day"), "day")) {
      return "tomorrow";
    } else {
      return `on ${date.format("ddd, MMM D")}`;
    }
  };

  const loadSchedulesForDate = async (date) => {
    // Check if data is already cached
    const cacheKey = `${date}_${type}`;
    if (dataCache[cacheKey]) {
      return dataCache[cacheKey];
    }

    // Check if already loading this date
    if (loadingDates.has(cacheKey)) {
      return null; // Already loading, don't duplicate request
    }

    try {
      setLoadingDates((prev) => new Set(prev).add(cacheKey));
      const data = await loadSchedules(type, date);

      // Cache the data
      setDataCache((prev) => ({
        ...prev,
        [cacheKey]: data,
      }));

      return data;
    } catch (e) {
      toast.error(`Something went wrong! ${e?.message}`);
      throw e;
    } finally {
      setLoadingDates((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
  };

  const processSchedulesData = (data) => {
    let now = moment.tz(timezoneRef.current);
    if (type === "classes") now = now.subtract(15, "minutes");
    else now = now.subtract(4, "hours");

    const toGo = data.filter((schedule) =>
      moment.tz(schedule.start, timezoneRef.current).isAfter(now),
    );
    const passed = data.filter((schedule) =>
      moment.tz(schedule.start, timezoneRef.current).isBefore(now),
    );

    return { toGo, passed };
  };

  const changeDate = async (direction) => {
    const newDate = moment
      .tz(selectedDate, timezoneRef.current)
      .add(direction === "forward" ? 1 : -1, "days")
      .format("YYYY-MM-DD");

    setSelectedDate(newDate);
  };

  useEffect(() => {
    const getSchedules = async () => {
      try {
        setLoading(true);
        setSchedules([]);
        setPassedSchedules([]);

        const data = await loadSchedulesForDate(selectedDate);
        if (data) {
          const { toGo, passed } = processSchedulesData(data);
          setSchedules(toGo);
          setPassedSchedules(passed);
        }

        setLoading(false);
      } catch (e) {
        setLoading(false);
        // Error already handled in loadSchedulesForDate
      }
    };

    if (selectedSiteId && selectedLocationId) getSchedules();
  }, [selectedSiteId, selectedLocationId, type, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToDetails = (data) => {
    setSelectedSchedule(data);
    navigate(`./${data.id}/details`);
  };

  const getImage = (sc) => {
    if (type === "classes") return sc.staffImage || "/placeholder.jpg";
    return sc.classImage || "/placeholder.jpg";
  };

  // Nothing left to check into — the heading carries the message instead of
  // asking which class they're here for.
  const isEmpty = !loading && schedules.length === 0;
  const emptyHeading = `No ${passedSchedules.length > 0 ? "more " : ""}${
    type === "classes" ? "classes" : "events"
  }`;
  const emptySubheading =
    type === "classes" ? formatRelativeDay(selectedDate) : "scheduled";

  // On an empty day the date navigator gives way to a single big "next day"
  // button. At the end of the bookable range there's no next day to offer, so
  // the navigator stays put — otherwise there'd be no way back.
  const showNextDayCta = isEmpty && type === "classes" && canGoForward;
  const nextDayCtaLabel =
    selectedDate === today
      ? "Check tomorrow's classes"
      : "Check the next day's classes";

  // Circular icon buttons — .btn's pill padding has to be overridden for the
  // arrows to sit in a true circle.
  const dateNavBtnClasses =
    "rounded-circle p-0 d-inline-flex align-items-center justify-content-center border-0";
  const dateNavBtnStyle = { width: 60, height: 60 };

  return (
    <>
      <div
        className="container d-md-flex h-100 p-0"
        style={{ minHeight: "100vh" }}
      >
        <GoBack />
        <div
          className="container-fluid class_canvas py-5"
          id="app-canvas"
          style={{ display: "block" }}
        >
          <div className="row py-5 align-items-center class-list" id="starter">
            <div className="col-11 col-xl-8 mx-auto">
              <div className="row">
                <div className="col-12 text-center">
                  {isEmpty ? (
                    <h2 className="pb-5" data-testid="class-list-heading">
                      {emptyHeading} {emptySubheading}
                    </h2>
                  ) : (
                    <h1
                      className="pb-4 fw-light"
                      data-testid="class-list-heading"
                    >
                      <b>Which {type === "classes" ? "class" : "event"}</b>
                      <br />
                      are you here for?
                    </h1>
                  )}

                  {/* Date Navigation - Only for classes */}
                  {type === "classes" && !showNextDayCta && (
                    <div
                      className="row justify-content-center align-items-center opacity-50"
                      id="date-navigator"
                    >
                      <div className="col-auto">
                        <button
                          className={`btn btn-outline-secondary ${dateNavBtnClasses}${
                            canGoBack ? "" : " invisible"
                          }`}
                          data-testid="date-nav-prev-btn"
                          onClick={() => changeDate("back")}
                          disabled={!canGoBack}
                          style={dateNavBtnStyle}
                        >
                          <FaChevronLeft />
                        </button>
                      </div>
                      <div className="col-auto px-4" style={{ width: 200 }}>
                        <h4 className="mb-0 fw-light">
                          {formatDisplayDate(selectedDate)}
                        </h4>
                      </div>
                      <div className="col-auto">
                        <button
                          className={`btn btn-outline-secondary ${dateNavBtnClasses}${
                            canGoForward ? "" : " invisible"
                          }`}
                          data-testid="date-nav-next-btn"
                          onClick={() => changeDate("forward")}
                          disabled={!canGoForward}
                          style={dateNavBtnStyle}
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  )}

                  {showNextDayCta && (
                    <div className="row justify-content-center mb-4">
                      <div className="col-auto">
                        <button
                          className="btn btn-primary btn-lg"
                          id="next-day-cta"
                          data-testid="next-day-cta-btn"
                          onClick={() => changeDate("forward")}
                        >
                          {nextDayCtaLabel}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col py-3" id="classes_listing">
                {schedules.map((sc, idx) => (
                  <div className="row py-2" role="button" key={idx}>
                    <div
                      className="col-12 p-0 bg-white shadow-sm rounded-4 border d-flex align-items-center overflow-hidden"
                      data-testid="class-item"
                      onClick={() => goToDetails(sc)}
                    >
                      <span
                        className="staff-image ratio ratio-1x1 flex-shrink-0"
                        style={{
                          backgroundImage: `url(${getImage(sc)})`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          backgroundSize: "cover",
                        }}
                      ></span>
                      <div className="px-4 py-3 flex-grow-1 overflow-hidden">
                        <p className="p-0 m-0">
                          {type !== "classes" &&
                            sc.startDateFormatted + " from "}
                          {sc.startTimeFormatted} - {sc.endTimeFormatted}
                        </p>
                        <h3 className="py-2 m-0 fw-bold text-truncate">
                          {sc.fullname || sc.name}
                        </h3>
                        <p className="p-0 m-0 item-info text-truncate">
                          <small> with </small>
                          {sc.staffName}
                          {sc.resourceName && <small> in </small>}
                          {sc.resourceName}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="row">
                    <div className="col">
                      <Skeleton
                        count={3}
                        height={150}
                        className="row my-3 rounded-4 mx-0"
                      />
                    </div>
                  </div>
                )}

                {passedSchedules.length > 0 && (
                  <div className="mt-md-4 pt-md-5 pb-3 text-center fw-light">
                    <i>--- Passed Classes ---</i>
                  </div>
                )}

                {passedSchedules.map((sc, idx) => (
                  <div className="row py-2" role="button" key={idx}>
                    <div
                      className="col-12 p-0 bg-white shadow-sm rounded-4 border opacity-50 d-flex align-items-center overflow-hidden"
                      data-testid="class-item-passed"
                      onClick={() => goToDetails(sc)}
                    >
                      <span
                        className="staff-image ratio ratio-1x1 flex-shrink-0"
                        style={{
                          backgroundImage: `url(${getImage(sc)})`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          backgroundSize: "cover",
                        }}
                      ></span>
                      <div className="px-4 py-3 flex-grow-1 overflow-hidden">
                        <p className="p-0 m-0">
                          {type !== "classes" &&
                            sc.startDateFormatted + " from "}
                          {sc.startTimeFormatted} - {sc.endTimeFormatted}
                        </p>
                        <h3 className="py-2 m-0 fw-bold text-truncate">
                          {sc.fullname || sc.name}
                        </h3>
                        <p className="p-0 m-0 item-info text-truncate">
                          <small> with </small>
                          {sc.staffName}
                          {sc.resourceName && <small> in </small>}
                          {sc.resourceName}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
